import React from 'react';
import { Button, Container } from '@seaters-app/ui';
import { Card, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams } from 'react-router-dom';
import { useOAuthLogin } from '../../../hooks/useOAuthLogin';

const { Title, Text } = Typography;

const OauthEmailVerificationRequired: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { provider } = useParams();

  // Get email from URL params (passed from VerifyAuthentication)
  const email = searchParams.get('email');

  // Use reusable OAuth login hook with state preservation
  const { redirectToOAuthLogin, isLoading } = useOAuthLogin(provider || '');

  const handleLogin = () => {
    redirectToOAuthLogin();
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 20px 40px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '700px',
          padding: '0 50px',
          backgroundColor: 'white',
        }}
      >
        <Title
          level={2}
          style={{
            textAlign: 'left',
            marginBottom: '20px',
            fontSize: '28px',
            fontWeight: '600',
          }}
        >
          {t(
            'email_verification_required_title',
            'Just one more step to secure your account'
          )}
        </Title>

        <div style={{ marginBottom: '16px' }}>
          <Text
            style={{
              fontSize: '16px',
              lineHeight: '1.6',
              display: 'block',
              marginBottom: '12px',
            }}
          >
            {t(
              'email_verification_description_1',
              'To finish signing in, we need to verify that you have access to'
            )}{' '}
            {t(
              'email_verification_description_2',
              'your email address. This is a one-time security check.'
            )}
          </Text>

          <Text
            style={{
              fontSize: '16px',
              lineHeight: '1.6',
              display: 'block',
            }}
          >
            {t(
              'email_verification_description_3',
              'We have sent a verification email to:'
            )}
          </Text>
        </div>

        {email && (
          <div
            style={{
              marginBottom: '20px',
              textAlign: 'left',
            }}
          >
            <Text strong style={{ fontSize: '16px' }}>
              ({email})
            </Text>
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <Text style={{ fontSize: '16px', lineHeight: '1.6' }}>
            {t(
              'email_verification_instruction',
              'Please open the email and click the confirmation link to continue. The link will expire in 60 minutes.'
            )}
          </Text>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <Text
            strong
            style={{ fontSize: '16px', display: 'block', marginBottom: '4px' }}
          >
            {t('email_verification_no_email', "Didn't receive the email?")}
          </Text>
          <Text style={{ fontSize: '16px', lineHeight: '1.6' }}>
            {t(
              'email_verification_check_spam',
              "Please check your spam or junk folder. If you still can't find it, you can request a new one by trying to log in again."
            )}
          </Text>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <Text
            strong
            style={{ fontSize: '16px', display: 'block', marginBottom: '4px' }}
          >
            {t(
              'email_verification_wrong_email',
              'Is the email address above incorrect?'
            )}
          </Text>
          <Text style={{ fontSize: '16px', lineHeight: '1.6' }}>
            {t(
              'email_verification_change_email',
              'Please change the email address in the platform you came from.'
            )}
          </Text>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <Text
            strong
            style={{ fontSize: '16px', display: 'block', marginBottom: '4px' }}
          >
            {t('email_verification_link_expired', 'What if the link expires?')}
          </Text>
          <Text style={{ fontSize: '16px', lineHeight: '1.6' }}>
            {t(
              'email_verification_retry',
              'Try logging in again, this will trigger a new email link.'
            )}
          </Text>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <Text
            strong
            style={{ fontSize: '16px', display: 'block', marginBottom: '4px' }}
          >
            {t(
              'email_verification_other_device',
              'Verified your email on another device?'
            )}
          </Text>
          <Text style={{ fontSize: '16px', lineHeight: '1.6' }}>
            {t(
              'email_verification_return',
              'Go back to the log in page and then you will be redirected to the platform.'
            )}
          </Text>
        </div>

        <Button
          type="primary"
          size="large"
          style={{
            width: '100%',
            height: '50px',
            fontSize: '16px',
            fontWeight: '500',
          }}
          onClick={handleLogin}
          loading={isLoading}
          disabled={isLoading}
        >
          {t('login_text_title', 'Log in')}
        </Button>
      </div>
    </div>
  );
};

export default OauthEmailVerificationRequired;
